import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { Vehicle } from '../../types';
import { CONSIGNMENT_EXPOSURE_LABELS } from '../../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const LOJA_NOME = 'NOME DA LOJA (CONSIGNATÁRIO)';
const LOJA_CNPJ = '00.000.000/0001-00';

const formatCurrency = (value: number) =>
  value > 0 ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'Não aplicável (consignado)';

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '________________';
  try {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? '________________' : format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return '________________';
  }
};

const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontSize: 11,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
  },
  paragraph: {
    textAlign: 'justify',
    marginBottom: 10,
    textIndent: 24,
  },
  bold: {
    fontWeight: 'bold',
  },
  clauseTitle: {
    fontWeight: 'bold',
    marginBottom: 6,
  },
  signatures: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 50,
    paddingTop: 10,
  },
  signatureLine: {
    width: 180,
    borderTopWidth: 1,
    borderTopColor: '#000',
    paddingTop: 8,
    textAlign: 'center',
    fontSize: 10,
  },
  footer: {
    marginTop: 20,
    fontSize: 9,
    color: '#666',
    textAlign: 'center',
  },
});

interface ContratoConsignacaoPDFProps {
  vehicle: Vehicle;
  dataEmissao?: string;
}

const ContratoConsignacaoPDFDoc: React.FC<ContratoConsignacaoPDFProps> = ({
  vehicle,
  dataEmissao = new Date().toISOString().split('T')[0],
}) => {
  const dataFormatada = formatDate(dataEmissao);
  const proprietario = vehicle.consignment_owner_name || '________________________________';
  const contato = vehicle.consignment_owner_contact || '________________________________';
  const comissao = vehicle.consignment_commission ?? 0;
  const comissaoTipo = vehicle.consignment_commission_type ?? 'percent';
  const exposicao = vehicle.consignment_exposure
    ? CONSIGNMENT_EXPOSURE_LABELS[vehicle.consignment_exposure]
    : '________________________________';
  const dataContrato = formatDate(vehicle.consignment_contract_date);

  return (
    <Document title="Contrato de Consignação de Veículo">
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>CONTRATO DE CONSIGNAÇÃO DE VEÍCULO</Text>

        <Text style={styles.paragraph}>
          Pelo presente instrumento particular de <Text style={styles.bold}>Consignação de Veículo</Text>, as partes
          abaixo qualificadas celebram o seguinte contrato:
        </Text>

        <Text style={styles.paragraph}>
          <Text style={styles.bold}>CONSIGNANTE (PROPRIETÁRIO DO VEÍCULO):</Text> {proprietario}, portador do CPF/CNPJ
          ________________, residente e domiciliado em ________________, telefone/contato: {contato}.
        </Text>
        <Text style={styles.paragraph}>
          <Text style={styles.bold}>CONSIGNATÁRIO (LOJA):</Text> {LOJA_NOME}, inscrita no CNPJ sob o nº {LOJA_CNPJ}, com
          sede em ________________.
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>DO OBJETO:</Text>
        <Text style={styles.paragraph}>
          O CONSIGNANTE entrega em consignação ao CONSIGNATÁRIO o veículo abaixo descrito, para que este promova a venda
          a terceiros, sem que haja transferência de propriedade até a efetiva venda e pagamento.
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>DADOS DO VEÍCULO:</Text>
        <Text style={styles.paragraph}>
          Marca: {vehicle.brand}; Modelo: {vehicle.model}; Ano: {vehicle.year}; Cor: {vehicle.color}; Placa:{' '}
          {vehicle.plate}; Chassi: {vehicle.chassis || '________________'}; Quilometragem: {vehicle.km || '________________'};
          Motor: {vehicle.motor || '________________'}; Câmbio: {vehicle.cambio || '________________'}.
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>FORMA DE EXPOSIÇÃO:</Text>
        <Text style={styles.paragraph}>O veículo ficará {exposicao}, conforme combinado entre as partes.</Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>VALOR MÍNIMO DE VENDA (PREÇO DE REFERÊNCIA):</Text>
        <Text style={styles.paragraph}>
          {vehicle.purchase_price > 0 ? formatCurrency(vehicle.purchase_price) : 'A combinar entre as partes'}.
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>COMISSÃO:</Text>
        <Text style={styles.paragraph}>
          O CONSIGNATÁRIO terá direito a comissão de{' '}
          <Text style={styles.bold}>
            {comissaoTipo === 'fixed'
              ? comissao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              : `${comissao}%`}
          </Text>
          {comissaoTipo === 'fixed'
            ? ', a ser descontada no ato do repasse ao CONSIGNANTE.'
            : ' sobre o valor efetivo da venda, a ser descontada no ato do repasse ao CONSIGNANTE.'}
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>PRAZO:</Text>
        <Text style={styles.paragraph}>
          O presente contrato tem vigência a partir de {dataContrato}, podendo ser prorrogado ou rescindido mediante
          acordo entre as partes, com comunicação por escrito.
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>RESPONSABILIDADES:</Text>
        <Text style={styles.paragraph}>
          O CONSIGNATÁRIO compromete-se a zelar pelo veículo durante a permanência em suas dependências (quando
          aplicável), realizar divulgação e negociação com compradores. O CONSIGNANTE declara ser legítimo proprietário
          do veículo e que o mesmo está livre de ônus e impedimentos. As partes concordam que a venda somente se
          consumará com a assinatura de contrato de compra e venda e repasse do valor líquido ao CONSIGNANTE, após a
          dedução da comissão e eventuais despesas previamente acordadas.
        </Text>

        <Text style={[styles.paragraph, styles.clauseTitle]}>LAUDO CAUTELAR / VISTORIA:</Text>
        <Text style={styles.paragraph}>
          As partes atestam que o estado de conservação do veículo foi verificado e anotado em vistoria/laudo, quando
          aplicável, fazendo parte integrante do presente contrato.
        </Text>

        {vehicle.consignment_notes && (
          <>
            <Text style={[styles.paragraph, styles.clauseTitle]}>OBSERVAÇÕES:</Text>
            <Text style={styles.paragraph}>{vehicle.consignment_notes}</Text>
          </>
        )}

        <Text style={styles.paragraph}>
          E, por estarem assim justos e contratados, firmam o presente em 2 (duas) vias de igual teor e forma.
        </Text>
        <Text style={styles.paragraph}>________________________, {dataFormatada}.</Text>

        <View style={styles.signatures}>
          <View style={styles.signatureLine}>
            <Text>CONSIGNANTE (Proprietário)</Text>
          </View>
          <View style={styles.signatureLine}>
            <Text>CONSIGNATÁRIO (Loja)</Text>
          </View>
        </View>

        <Text style={styles.footer}>
          Documento gerado automaticamente. Recomenda-se revisão pelo setor jurídico antes do uso.
        </Text>
      </Page>
    </Document>
  );
};

export default ContratoConsignacaoPDFDoc;
